import {
  type Chain,
  type DynamicClient,
  type WalletProviderMetadata,
} from '@dynamic-labs-sdk/client';
import type { WalletProviderConnectAddress } from '@dynamic-labs-sdk/client/core';
import {
  DYNAMIC_ICONIC_SPRITE_URL,
  assertDefined,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import type { BitcoinPaymentTypes } from '@leather.io/rpc';
import { Psbt } from 'bitcoinjs-lib';

import type { BitcoinWalletProvider } from '../../../../BitcoinWalletProvider.types';
import { bitcoinExecuteCheckoutTransaction } from '../../../../utils/bitcoinExecuteCheckoutTransaction';
import { bitcoinTransferAmount } from '../../../../utils/bitcoinTransferAmount';
import { convertNetworkIdForPsbt } from '../../../../utils/convertNetworkIdForPsbt';
import type { LeatherBitcoinInjectedProvider } from '../LeatherBitcoinInjectedProvider.types';
import { convertNetworkIdToLeatherNetworkName } from '../convertNetworkIdToLeatherNetworkName';

export const LEATHER_METADATA: WalletProviderMetadata = {
  displayName: 'Leather',
  icon: `${DYNAMIC_ICONIC_SPRITE_URL}#leather`,
  supportedHardwareWalletVendors: ['ledger'],
};

type CreateLeatherBitcoinWalletProviderParams = {
  dynamicClient: DynamicClient;
  injectedProvider: LeatherBitcoinInjectedProvider;
};

export const createLeatherBitcoinWalletProvider = ({
  dynamicClient,
  injectedProvider,
}: CreateLeatherBitcoinWalletProviderParams): BitcoinWalletProvider => {
  const chain: Chain = 'BTC';

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain,
    displayName: LEATHER_METADATA.displayName,
    walletProviderType,
  });

  const getNetworkPayload = async () => {
    const { networkId } = await getActiveNetworkId();
    const networkName = convertNetworkIdToLeatherNetworkName(networkId);

    return {
      networkId,
      networkName,
    };
  };

  const connect: BitcoinWalletProvider['connect'] = async () => {
    const response = await injectedProvider
      .request('getAddresses')
      .catch(({ error }) => {
        throw error;
      });

    const connectedAddresses = response.result.addresses;

    const addresses: WalletProviderConnectAddress[] = [];

    const ordinalsAddress = connectedAddresses?.find(
      (address) => address.type === 'p2tr'
    );

    if (ordinalsAddress) {
      addresses.push({
        address: ordinalsAddress.address,
        publicKey: ordinalsAddress.publicKey,
        type: 'ordinals',
      });
    }

    const paymentAddress = connectedAddresses?.find(
      (address) => address.type === 'p2wpkh'
    );

    if (paymentAddress) {
      addresses.push({
        address: paymentAddress.address,
        publicKey: paymentAddress.publicKey,
        type: 'payment',
      });
    }

    return {
      addresses,
    };
  };

  const getActiveNetworkId: BitcoinWalletProvider['getActiveNetworkId'] =
    async () =>
      getActiveNetworkIdFromLastKnownRegistry({
        client: dynamicClient,
        walletProviderKey: key,
      });

  // there's no specific method to get connected addresses in Leather
  const getConnectedAddresses: BitcoinWalletProvider['getConnectedAddresses'] =
    async () => {
      const accounts = await connect();
      return {
        addresses: accounts.addresses.map((address) => address.address),
      };
    };

  const sendBitcoin: BitcoinWalletProvider['sendBitcoin'] = async ({
    transaction,
  }) => {
    const { networkName } = await getNetworkPayload();

    const response = await injectedProvider
      .request('sendTransfer', {
        account: 0,
        address: transaction.recipientAddress,
        amount: transaction.amount.toString(),
        // network is required, so fallback to mainnet
        network: networkName ?? 'mainnet',
      })
      .catch(({ error }) => {
        throw error;
      });

    return {
      transactionId: response.result.txid,
    };
  };

  const signMessage: BitcoinWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
    addressType,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    let paymentType: BitcoinPaymentTypes = 'p2tr';
    if (addressType === 'payment') {
      paymentType = 'p2wpkh';
    }

    const { networkName } = await getNetworkPayload();

    const response = await injectedProvider
      .request('signMessage', {
        message,
        network: networkName,
        paymentType,
      })
      .catch(({ error }) => {
        throw error;
      });

    return {
      signature: response.result.signature,
    };
  };

  const signPsbt: BitcoinWalletProvider['signPsbt'] = async ({ request }) => {
    const { networkId, networkName } = await getNetworkPayload();
    const psbtNetwork = convertNetworkIdForPsbt(networkId);

    const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64, {
      network: psbtNetwork,
    });

    const signAtIndex = request.signature
      ?.flatMap((sig) => sig.signingIndexes)
      .filter(Number.isInteger) as number[] | undefined;

    const response = await injectedProvider
      .request('signPsbt', {
        account: 0,
        allowedSighash: request.allowedSighash,
        broadcast: false,
        hex: psbtFromBase64.toHex(),
        // network is required, so fallback to mainnet
        network: networkName ?? 'mainnet',
        signAtIndex,
      })
      .catch(({ error }) => {
        throw error;
      });

    const signedPsbtHex = response.result.hex;

    return {
      signedPsbt: Psbt.fromHex(signedPsbtHex, {
        network: psbtNetwork,
      }).toBase64(),
    };
  };

  const signPsbts: BitcoinWalletProvider['signPsbts'] = async ({
    requests,
    walletAccount,
  }) => {
    const signedPsbts: string[] = [];

    for (const request of requests) {
      const signedPsbtResponse = await signPsbt({ request, walletAccount });

      if (signedPsbtResponse) {
        signedPsbts.push(signedPsbtResponse.signedPsbt);
      }
    }

    return {
      signedPsbts,
    };
  };

  return {
    chain,
    connect,
    executeCheckoutTransaction: (args) =>
      bitcoinExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(LEATHER_METADATA.displayName),
    key,
    metadata: LEATHER_METADATA,
    sendBitcoin,
    signMessage,
    signPsbt,
    signPsbts,
    transferAmount: (args) => bitcoinTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
